import { useContext } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Paper, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { AuthContext } from "../../context/user.context";
import {
  getFirstAllowedRoute,
  isRouteAllowed,
} from "../../utils/routing/routePermissions";

export const AccessDeniedScreen = ({ message }) => {
  const { firebaseUser, retrySession, signOut } =
    useContext(AuthContext);
  const navigate = useNavigate();
  const switchAccount = async () => {
    await signOut();
    navigate("/sign-in", { replace: true });
  };
  return (
    <Box
      component="main"
      sx={{ minHeight: "100vh", bgcolor: "background.default", display: "grid", placeItems: "center", p: 2 }}
    >
      <Paper
        variant="outlined"
        sx={{ width: "100%", maxWidth: 520, p: { xs: 3, sm: 4 }, borderRadius: 3, borderColor: "divider", bgcolor: "background.paper" }}
      >
        <LockOutlinedIcon color="warning" sx={{ fontSize: 38, mb: 2 }} />
        <Typography variant="h5" component="h1" color="text.primary" gutterBottom>
          Acceso restringido
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {message ||
            "Tu cuenta no tiene permisos para usar esta sección de AutoApp."}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 3 }}>
        {firebaseUser && (
          <Button variant="outlined" onClick={retrySession}>
            Revalidar acceso
          </Button>
        )}
        <Button variant="contained" onClick={switchAccount}>
          Ingresar con otra cuenta
        </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export const RequireAppAccess = ({ requiredApp, children }) => {
  const { currentUser, sessionState } = useContext(AuthContext);

  if (!currentUser) return <Navigate to="/access-denied" replace />;
  if (sessionState === "selecting") return <Navigate to="/" replace />;
  if (sessionState !== "ready") {
    return <Navigate to="/access-denied" replace />;
  }

  // El contexto viene firmado por el resolver. Nunca se infiere desde `type`.
  if (requiredApp && currentUser.activeApp !== requiredApp) {
    return <Navigate to="/access-denied" replace />;
  }

  const allowedApps = currentUser.allowedApps || [];
  if (requiredApp && !allowedApps.includes(requiredApp)) {
    return <Navigate to="/access-denied" replace />;
  }

  return children;
};

export const RequirePermission = ({ path, children }) => {
  const { currentUser, sessionState } = useContext(AuthContext);
  const location = useLocation();

  if (!currentUser || sessionState !== "ready") {
    return <Navigate to="/access-denied" replace />;
  }

  if (!isRouteAllowed(path || location.pathname, currentUser)) {
    const fallback = getFirstAllowedRoute(currentUser);
    return <Navigate to={fallback || "/access-denied"} replace />;
  }

  return children;
};

export const FirstAllowedRoute = () => {
  const { currentUser, sessionState } = useContext(AuthContext);
  if (!currentUser || sessionState !== "ready") {
    return <Navigate to="/access-denied" replace />;
  }
  const firstRoute = getFirstAllowedRoute(currentUser);
  return firstRoute ? (
    <Navigate to={firstRoute} replace />
  ) : (
    <Navigate to="/access-denied" replace />
  );
};
